import {
  ApiError,
  apiGetOrNull,
  apiRequest,
  apiRequestWithEtag,
  type IEtagged
} from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  IConcernCriterion,
  IEscalationLevels,
  IEscalationLevelsBody,
  IConcernCriterionBody,
  IManualWorkItem,
  IMyTasksPage,
  INewConcernCriterion,
  IWorkItemAssignee,
  IWorkItemAssignment,
  IWorkItemAssignments,
  IWorkItemCounts,
  IWorkItemCountsQuery,
  IWorkItemDecision,
  IWorkItemDetail,
  IWorkItemPatch,
  IWorkItemQuery,
  IWorkItemRow,
  IWorkItemSummary,
  IWorkItemWithEvents,
  MyTaskFlag,
  MyTaskType
} from './work-item-contracts';

/**
 * survey-api's work-item queue and the concern criteria its rules read (SR-222, the work-items
 * design's §7): `/api/v1/admin/work-items/*`, the two routes under a participant
 * (`…/participants/{id}/work-items`, the household's history and New task) and
 * `/api/v1/admin/concern-criteria*`. The sole reader of all three.
 *
 * The two participant routes are read here rather than from `participants-api.ts`, although they
 * sit under its prefix, for survey-api's own reason: they are the queue's routes, mapped in its
 * file (`AdminWorkItemEndpoints.cs`), and answer its rows. Split by prefix instead, a work item's
 * row would have two readers. The criteria are here too — their reads are `responses.browse`
 * like the queue's, their writes `compliance.configure`, admin alone — because the queue is what
 * they raise, and one module is what a screen of both imports.
 *
 * **Concurrency is the server's, quoted back as read.** The detail comes with its revision as
 * the `ETag`, which `patchWorkItem` and `reopenWorkItem` send as `If-Match` (survey-api answers
 * 428 without it and 409 `revision-conflict` on a stale one) and whose answer carries the next
 * one. It is opaque here (F1's R1): never parsed into `row.revision`. A decision takes none —
 * survey-api's choice, since one header cannot carry 200 revisions — and neither do the
 * criteria, whose edits append a version and collide on its uniqueness instead.
 *
 * Nothing here decides which move a state takes (`WORK_ITEM_MOVES` in `work-items.ts` is the
 * one mirror of that), judges a predicate, or computes a due date.
 */

const BASE = '/api/v1/admin';

/** Values that could only reach a different route: `encodeURIComponent` leaves `.` and `..`
 *  alone, and the URL parser behind `fetch` then removes them — `work-items/.` would be the
 *  list, `concern-criteria/..` the admin root. The participants-api rule. */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

/** One path segment, or `null` for a value that cannot be one — see `NOT_A_SEGMENT`. */
function segment(value: string): Nullable<string> {
  return NOT_A_SEGMENT.has(value) ? null : encodeURIComponent(value);
}

/** One path segment, refused where `segment` answers `null`: a write is never sent to another
 *  route. Writes take server-issued ids and codes, so this is a guard, not a branch. */
function writeSegment(value: string): string {
  const encoded = segment(value);
  if (encoded === null) throw new Error(`"${value}" cannot be sent as one path segment.`);
  return encoded;
}

function queryString(params: URLSearchParams): string {
  const rendered = params.toString();
  return rendered ? `?${rendered}` : '';
}

/** The list's query: an array repeats its parameter, `''` and `undefined` are absent. */
function listQuery(query: IWorkItemQuery): string {
  const params = new URLSearchParams();
  const many = (name: string, values: string[] | undefined) =>
    values?.forEach((value) => params.append(name, value));
  const one = (name: string, value: string | number | undefined) => {
    if (value !== undefined && value !== '') params.append(name, String(value));
  };
  many('survey', query.survey);
  many('state', query.state);
  many('trigger', query.trigger);
  one('movedFirm', query.movedFirm === undefined ? undefined : String(query.movedFirm));
  many('criterion', query.criterion);
  one('assignee', query.assignee);
  one('participantId', query.participantId);
  one('refreshRunId', query.refreshRunId);
  many('fields', query.fields);
  one('sort', query.sort);
  many('flag', query.flag);
  one('since', query.since);
  one('q', query.q);
  one('page', query.page);
  one('pageSize', query.pageSize);
  return queryString(params);
}

function pageQuery(query: { page?: number; pageSize?: number }): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
  return queryString(params);
}

function ifMatch(etag: string): Record<string, string> {
  return { 'If-Match': etag };
}

export const workItemsApi = {
  /** The queue, one page. An unknown filter value, sort or `fields` code is 400
   *  `invalid-query` with a `problems[]` entry per offence. */
  listWorkItems(query: IWorkItemQuery, signal?: AbortSignal): Promise<IPage<IWorkItemRow>> {
    return apiRequest<IPage<IWorkItemRow>>(`${BASE}/work-items${listQuery(query)}`, { signal });
  },

  /** My tasks (SR-424): the caller's unclosed work items, then every Lists row assigned to them,
   *  one page of the merge and the count of each type; `type` narrows to one, and
   *  `participantId` with `survey` to one Lists row — a task's own page; `flag`, repeated, to the
   *  tasks raising any of them, and `since` says which are new. */
  myTasks(
    query: {
      type?: MyTaskType;
      participantId?: string;
      survey?: string;
      page?: number;
      pageSize?: number;
      since?: Nullable<string>;
      flag?: readonly MyTaskFlag[];
    },
    signal?: AbortSignal
  ): Promise<IMyTasksPage> {
    const params = new URLSearchParams();
    if (query.type) params.set('type', query.type);
    if (query.participantId) params.set('participantId', query.participantId);
    if (query.survey) params.set('survey', query.survey);
    if (query.page !== undefined) params.set('page', String(query.page));
    if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
    if (query.since) params.set('since', query.since);
    for (const flag of query.flag ?? []) params.append('flag', flag);
    const search = params.toString();
    return apiRequest<IMyTasksPage>(`${BASE}/my-tasks${search ? `?${search}` : ''}`, { signal });
  },

  /** Each queue's items by state — only the queues that hold an item. */
  workItemSummary(signal?: AbortSignal): Promise<IWorkItemSummary> {
    return apiRequest<IWorkItemSummary>(`${BASE}/work-items/summary`, { signal });
  },

  /** The queue's counts under the list's filters (SR-485): each state's items with their
   *  overdue, unassigned and own, and each queue's by state, the survey filter left out. */
  workItemCounts(query: IWorkItemCountsQuery, signal?: AbortSignal): Promise<IWorkItemCounts> {
    return apiRequest<IWorkItemCounts>(`${BASE}/work-items/counts${listQuery(query)}`, { signal });
  },

  /** Whom an item can be assigned to: active staff and admin, by email. Its own route because
   *  the account list is admin-only. */
  workItemAssignees(signal?: AbortSignal): Promise<IWorkItemAssignee[]> {
    return apiRequest<IWorkItemAssignee[]>(`${BASE}/work-items/assignees`, { signal });
  },

  /**
   * One item with its events and its `ETag`, or `null` for no such item — a 404, which the
   * route's `guid` constraint also answers for an id that is not one. The pane's id comes from
   * the address bar, so `''`, `.` and `..` are answered `null` here, unsent: each would reach
   * another route (the list, or the admin root).
   */
  getWorkItem(
    workItemId: string,
    signal?: AbortSignal
  ): Promise<Nullable<IEtagged<IWorkItemDetail>>> {
    const id = segment(workItemId);
    if (id === null) return Promise.resolve(null);
    return apiRequestWithEtag<IWorkItemDetail>(`${BASE}/work-items/${id}`, { signal }).catch(
      (error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    );
  },

  /** The household's items, any state, each with its events, newest first (CMP-13). Throws on a
   *  404, as the hub's other reads do: its caller has read the hub, so an unknown participant
   *  here is the household going between two reads. */
  householdWorkItems(
    participantId: string,
    query: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<IWorkItemWithEvents>> {
    return apiRequest<IPage<IWorkItemWithEvents>>(
      `${BASE}/participants/${writeSegment(participantId)}/work-items${pageQuery(query)}`,
      { signal }
    );
  },

  /** One assignee, or nobody, for the ticked items or every item a filter matches (SR-466), all
   *  or nothing. A filter matching more than 2,000 is 422 `invalid-action` at `filter`. `restore`
   *  undoes one: an item a colleague moved since is left, and counted in `left`. */
  assignWorkItems(body: IWorkItemAssignment): Promise<IWorkItemAssignments> {
    return apiRequest<IWorkItemAssignments>(`${BASE}/work-items/assignments`, {
      method: 'POST',
      body
    });
  },

  /**
   * One decision over 1–200 items, all or nothing; the answer is every item's row. 422
   * `invalid-action` names each unknown id; 409 `invalid-transition` every id whose state refuses
   * the decision (`transitionRefusal` in `work-items.ts` reads it); 409 `revision-conflict`, with
   * no revision, a batch that lost a race.
   */
  decideWorkItems(body: IWorkItemDecision): Promise<{ items: IWorkItemRow[] }> {
    return apiRequest<{ items: IWorkItemRow[] }>(`${BASE}/work-items/decisions`, {
      method: 'POST',
      body
    });
  },

  /** The assignee, the follow-up, or both, under `If-Match`; the answer's `ETag` is the next
   *  write's. */
  patchWorkItem(
    workItemId: string,
    etag: string,
    patch: IWorkItemPatch
  ): Promise<IEtagged<IWorkItemRow>> {
    return apiRequestWithEtag<IWorkItemRow>(`${BASE}/work-items/${writeSegment(workItemId)}`, {
      method: 'PATCH',
      body: patch,
      headers: ifMatch(etag)
    });
  },

  /** A reviewed item back to `open`, with a reason, under `If-Match`. */
  reopenWorkItem(workItemId: string, etag: string, reason: string): Promise<IEtagged<IWorkItemRow>> {
    return apiRequestWithEtag<IWorkItemRow>(
      `${BASE}/work-items/${writeSegment(workItemId)}/reopenings`,
      { method: 'POST', body: { reason }, headers: ifMatch(etag) }
    );
  },

  /** New task (CMP-15): a manual item on the participant, in the queue survey-api derives for
   *  it. 201 with the row. */
  raiseWorkItem(participantId: string, body: IManualWorkItem): Promise<IWorkItemRow> {
    return apiRequest<IWorkItemRow>(
      `${BASE}/participants/${writeSegment(participantId)}/work-items`,
      { method: 'POST', body }
    );
  },

  /** The escalation hierarchy (SR-410) — `responses.browse`. */
  escalationLevels(signal?: AbortSignal): Promise<IEscalationLevels> {
    return apiRequest<IEscalationLevels>(`${BASE}/escalation-levels`, { signal });
  },

  /** Every level at once, in order — `compliance.configure`, as the criteria's writes. 400
   *  `invalid-body` keys each problem `levels[i]`. Answers the levels saved. */
  saveEscalationLevels(body: IEscalationLevelsBody): Promise<IEscalationLevels> {
    return apiRequest<IEscalationLevels>(`${BASE}/escalation-levels`, { method: 'PUT', body });
  },

  /** Every version of every criterion: the seed's codes in seed order, then the others by code,
   *  each code's current version first. */
  listConcernCriteria(signal?: AbortSignal): Promise<IConcernCriterion[]> {
    return apiRequest<IConcernCriterion[]>(`${BASE}/concern-criteria`, { signal });
  },

  /** A code's current version (B8), or `null` for no such code. Its `ETag` is the version, which
   *  no write quotes back, so it is not kept. */
  getConcernCriterion(code: string, signal?: AbortSignal): Promise<Nullable<IConcernCriterion>> {
    const encoded = segment(code);
    if (encoded === null) return Promise.resolve(null);
    return apiGetOrNull<IConcernCriterion>(`${BASE}/concern-criteria/${encoded}`, { signal });
  },

  /** A new code, version 1 — admin. 409 `code-in-use` for a code that exists. */
  createConcernCriterion(body: INewConcernCriterion): Promise<IConcernCriterion> {
    return apiRequest<IConcernCriterion>(`${BASE}/concern-criteria`, { method: 'POST', body });
  },

  /** The code's next version — admin. Two at once collide: 409 `code-in-use`. */
  reviseConcernCriterion(code: string, body: IConcernCriterionBody): Promise<IConcernCriterion> {
    return apiRequest<IConcernCriterion>(`${BASE}/concern-criteria/${writeSegment(code)}`, {
      method: 'PUT',
      body
    });
  }
};
